import { Component } from '@theme/component';
import { SlideshowSelectEvent } from '@theme/events';

/**
 * Comparison sliders component
 *
 * @typedef {Object} Refs
 * @property {HTMLElement[]} [thumbnails]
 * @property {import('slideshow').Slideshow} comparisonSlidersSlideshow
 *
 * @extends {Component<Refs>}
 */
class ComparisonSlidersComponent extends Component {
  requiredRefs = ['comparisonSlidersSlideshow'];

  connectedCallback() {
    super.connectedCallback();

    this.refs.comparisonSlidersSlideshow?.addEventListener(
      SlideshowSelectEvent.eventName,
      this.#handleSlideshowSelect
    );

    const { thumbnails } = this.refs;
    if (!thumbnails?.length) return;
    for (const thumbnail of thumbnails) {
      thumbnail.addEventListener('click', this.#handleThumbnailClick);
      thumbnail.addEventListener('keydown', this.#handleThumbnailKeydown);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.refs.comparisonSlidersSlideshow?.removeEventListener(
      SlideshowSelectEvent.eventName,
      this.#handleSlideshowSelect
    );

    const { thumbnails } = this.refs;
    if (!thumbnails?.length) return;

    for (const thumbnail of thumbnails) {
      thumbnail.removeEventListener('click', this.#handleThumbnailClick);
      thumbnail.removeEventListener('keydown', this.#handleThumbnailKeydown);
    }
  }

  #handleThumbnailClick = (/** @type {MouseEvent} */ event) => {
    event.preventDefault();
    const index = this.#getThumbnailIndex(/** @type {HTMLElement} */ (event.currentTarget));
    if (index === null) return;
    this.#selectSlider(index);
  };

  #handleThumbnailKeydown = (/** @type {KeyboardEvent} */ event) => {
    const target = /** @type {HTMLElement} */ (event.currentTarget);
    if (target.getAttribute('role') !== 'tab') return;

    const { thumbnails } = this.refs;
    if (!thumbnails?.length) return;

    const index = thumbnails.indexOf(target);
    if (index === -1) return;

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.#selectSlider(index);
      return;
    }

    /** @type {Record<string, number | undefined>} */
    const navMap = {
      ArrowLeft: -1,
      ArrowRight: 1,
      Home: -index,
      End: thumbnails.length - 1 - index,
    };

    const offset = navMap[event.key];
    if (offset === undefined) return;
    
    const nextIndex = index + offset;
    if (nextIndex < 0 || nextIndex >= thumbnails.length) return;

    event.preventDefault();
    this.#selectSlider(nextIndex, { focus: true });
  };

  /**
   * Keeps tab UI in sync when the slideshow index changes without a thumbnail click.
   *
   * @param {SlideshowSelectEvent} event
   */
  #handleSlideshowSelect = (/** @type {SlideshowSelectEvent} */ event) => {
    const { trigger, index } = event.detail;
    if (trigger !== 'scroll' && trigger !== 'drag') return;

    this.#syncTabState(index);
  };

  /**
   * @param {HTMLElement | null} thumbnail
   * @returns {number | null}
   */
  #getThumbnailIndex(thumbnail) {
    const { thumbnails } = this.refs;
    if (!thumbnail || !thumbnails?.length) return null;
    const index = thumbnails.indexOf(thumbnail);
    return index === -1 ? null : index;
  }

  /**
   * @param {number} index
   * @param {{ focus?: boolean }} [options]
   */
  #selectSlider(index, { focus = false } = {}) {
    this.refs.comparisonSlidersSlideshow.select(index, undefined, { animate: false });
    this.#syncTabState(index, { focus });
  }

  /**
   * @param {number} index
   * @param {{ focus?: boolean }} [options]
   */
  #syncTabState(index, { focus = false } = {}) {
    this.#activateThumbnail(index, { focus });
    this.#updateTabPanels(index);
  }

  /**
   * @param {number} index
   */
  #updateTabPanels(index) {
    const { slides } = this.refs.comparisonSlidersSlideshow.refs;
    if (!slides?.length) return;

    for (const [slideIndex, slide] of slides.entries()) {
      const isActive = slideIndex === index;
      slide.toggleAttribute('inert', !isActive);
    }
  }

  /**
   * @param {number} index
   * @param {{ focus?: boolean }} [options]
   */
  #activateThumbnail(index, { focus = false } = {}) {
    const { thumbnails } = this.refs;
    if (!thumbnails?.length) return;

    for (const thumbnail of thumbnails) {
      thumbnail.setAttribute('aria-selected', 'false');
      thumbnail.setAttribute('tabindex', '-1');
    }

    const activeThumbnail = /** @type {HTMLElement | undefined} */ (thumbnails[index]);
    activeThumbnail?.setAttribute('aria-selected', 'true');
    activeThumbnail?.setAttribute('tabindex', '0');

    if (focus) {
      activeThumbnail?.focus();
    }
  }
}

if (!customElements.get('comparison-sliders-component')) {
  customElements.define('comparison-sliders-component', ComparisonSlidersComponent);
}
